import React, { forwardRef } from 'react';

export type InputVariant = 'default' | 'outlined' | 'glass';

export interface MPInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  variant?: InputVariant;
  inputSize?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const variantClasses: Record<InputVariant, string> = {
  default: [
    'mp-tw-bg-transparent mp-tw-text-mp-text',
    'mp-tw-border-0 mp-tw-border-b mp-tw-border-solid mp-tw-border-mp-border',
    'mp-tw-rounded-none',
    'focus:mp-tw-border-b-mp-orange',
    'placeholder:mp-tw-text-mp-text-muted',
  ].join(' '),
  outlined: [
    'mp-tw-bg-transparent mp-tw-text-mp-text',
    'mp-tw-border-2 mp-tw-border-solid mp-tw-border-mp-border',
    'mp-tw-rounded-mp-lg',
    'focus:mp-tw-border-mp-orange',
    'placeholder:mp-tw-text-mp-text-muted',
  ].join(' '),
  glass: [
    'mp-tw-text-mp-grey-300',
    'mp-tw-border mp-tw-border-solid',
    'mp-tw-rounded-mp-2xl',
    'focus:mp-tw-border-mp-orange',
    'placeholder:mp-tw-text-mp-grey-400',
  ].join(' '),
};

const glassStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.1)',
  borderColor: 'rgba(255, 255, 255, 0.2)',
};

const sizeClasses = {
  sm: 'mp-tw-px-3 mp-tw-py-2 mp-tw-text-mp-sm',
  md: 'mp-tw-px-4 mp-tw-py-3 mp-tw-text-mp-base',
  lg: 'mp-tw-px-5 mp-tw-py-4 mp-tw-text-mp-md',
};

const MPInput = forwardRef<HTMLInputElement, MPInputProps>(({
  label,
  error,
  hint,
  variant = 'default',
  inputSize = 'md',
  fullWidth = true,
  className = '',
  id,
  ...rest
}, ref) => {
  const inputId = id || `mp-input-${label?.toLowerCase().replace(/\s+/g, '-') || 'field'}`;

  const inputClasses = [
    'mp-tw-w-full mp-tw-outline-none',
    'mp-tw-transition-all mp-tw-duration-mp-normal mp-tw-ease-mp-ease',
    'focus:mp-focus-ring',
    variantClasses[variant],
    sizeClasses[inputSize],
    error ? 'mp-tw-border-mp-error' : '',
    'disabled:mp-tw-opacity-50 disabled:mp-tw-cursor-not-allowed',
  ].filter(Boolean).join(' ');

  return (
    <div className={`mp-tw-flex mp-tw-flex-col mp-tw-gap-1.5 ${fullWidth ? 'mp-tw-w-full' : ''} ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="mp-tw-text-mp-sm mp-tw-font-semibold mp-tw-text-mp-text"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        className={inputClasses}
        style={variant === 'glass' ? glassStyle : undefined}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        {...rest}
      />
      {error && (
        <span
          id={`${inputId}-error`}
          className="mp-tw-text-mp-xs mp-tw-text-mp-error"
          role="alert"
        >
          {error}
        </span>
      )}
      {hint && !error && (
        <span
          id={`${inputId}-hint`}
          className="mp-tw-text-mp-xs mp-tw-text-mp-text-muted"
        >
          {hint}
        </span>
      )}
    </div>
  );
});

MPInput.displayName = 'MPInput';

export default MPInput;
